import { Component, computed, effect, inject, signal } from '@angular/core';
import { NetworkStatusService } from '../../core/tenant/../offline/network-status.service';
import { OfflineOrderService } from '../../core/offline/offline-order.service';
import { lineasPedidas, tituloDelRechazo } from '../../core/offline/rechazos-definitivos';
import { OFFLINE_DB, OfflineDb, db } from '../../core/offline/db';
import { pesos } from '../../core/ui/pesos';
import { SessionService } from '../../core/tenant/session.service';
import Swal from 'sweetalert2';
import { FeatureService } from '../../core/tenant/feature.service';
import { AdquirenteService } from '../../core/factura/adquirente.service';

/**
 * Banner de estado de conectividad/sincronización (F2, Inc.4). Muestra:
 *  - OFFLINE: aviso de que las ventas se guardan y sincronizan luego.
 *  - ONLINE con pendientes: "sincronizando N…".
 *  - Órdenes RECHAZADAS (FAILED): aviso de atención (reintentar/re-login).
 *  - Ventas de OTRA cuenta esperando en este equipo (F2.2): se envían al entrar con esa cuenta.
 *  - ONLINE y todo al día: no muestra nada.
 */
@Component({
  selector: 'app-connection-banner',
  standalone: true,
  imports: [],
  template: `
    <!-- M34: las bandas de facturación, apiladas encima del aviso de conexión para que se lean a la vez. -->
    @if (facturasAtrasadas() || textoDelRango()) {
      <div class="conn-pila">
        @if (textoDelRango(); as texto) {
          <div class="conn-banner failed" role="status" data-testid="rango-de-facturas">
            <span class="dot"></span><span class="conn-text">{{ texto }}</span>
          </div>
        }
        @if (facturasAtrasadas(); as texto) {
          <div class="conn-banner failed" role="status" data-testid="facturas-atrasadas">
            <span class="dot"></span><span class="conn-text">{{ texto }}</span>
          </div>
        }
      </div>
    }
    @if (state(); as s) {
      <div class="conn-banner" role="status" aria-live="polite"
           [class.offline]="s.kind === 'offline'"
           [class.failed]="s.kind === 'failed'" [class.syncing]="s.kind === 'syncing'"
           [class.otros]="s.kind === 'otros'">
        <span class="dot"></span>
        <span class="conn-text">{{ s.text }}</span>
        @if (s.kind === 'failed') {
          @if (s.definitivas > 0) {
            <button type="button" class="conn-action" (click)="verRechazadas()" data-testid="ver-venta">
              Ver venta{{ s.definitivas === 1 ? '' : 's' }}
            </button>
          }
          @if (s.definitivas < s.total) {
          <button type="button" class="conn-action" [disabled]="reintentando()"
                  (click)="reintentar()">
            {{ reintentando() ? 'Reintentando…' : 'Reintentar ahora' }}
          </button>
          }
        }
      </div>
    }
  `,
  styles: [
    `
      /* FLOTANTE (N2/6.2): antes el aviso de "sin conexión" era un modal que
         bloqueaba el cobro. Ahora avisa sin interrumpir la venta. */
      .conn-banner {
        position: fixed;
        left: 50%;
        bottom: 1.25rem;
        transform: translateX(-50%);
        z-index: 1200;
        display: flex;
        align-items: center;
        gap: var(--esp-2);
        max-width: min(92vw, 40rem);
        padding: var(--esp-2) var(--esp-4);
        border-radius: var(--radio-pastilla);
        box-shadow: 0 8px 24px rgb(0 0 0 / 25%);
        font-size: var(--texto-sm);
        font-weight: var(--peso-semi);
        color: #fff;
        font-family: Inter, Segoe UI, sans-serif;
      }
      .conn-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .conn-banner .dot {
        flex: 0 0 auto;
        width: 8px;
        height: 8px;
        border-radius: var(--radio-redondo);
        background: currentColor;
        opacity: 0.85;
      }
      .conn-action {
        flex: 0 0 auto;
        padding: var(--esp-1) var(--esp-3);
        border: 1px solid rgb(255 255 255 / 55%);
        border-radius: var(--radio-pastilla);
        background: transparent;
        color: inherit;
        font: inherit;
        font-size: var(--texto-xs);
        cursor: pointer;
      }
      .conn-action:hover:not(:disabled) { background: rgb(255 255 255 / 15%); }
      .conn-action:disabled { opacity: 0.6; cursor: default; }
      .offline { background: #b45309; } /* ámbar oscuro */
      .failed { background: #b91c1c; } /* rojo */
      .syncing { background: #1e3a8a; } /* azul */
      .otros { background: #475569; } /* pizarra: no es un fallo, es un aviso */
      /* M34: encima del aviso de conexión; cada banda en su fila (dentro de la pila no flotan sueltas). */
      .conn-pila { position: fixed; left: 50%; bottom: 4.25rem; transform: translateX(-50%); z-index: 1200;
        display: flex; flex-direction: column; gap: var(--esp-2); width: min(92vw, 40rem); }
      .conn-pila .conn-banner { position: static; transform: none; max-width: none; }
      .conn-pila .conn-text { white-space: normal; }
      @media (max-width: 640px) { .conn-pila { left: 0.75rem; right: 0.75rem; transform: none; width: auto; } }

      @media (max-width: 640px) {
        .conn-banner { left: 0.75rem; right: 0.75rem; transform: none; max-width: none; }
        .conn-text { white-space: normal; }
      }
    `,
  ],
})
export class ConnectionBannerComponent {
  private net = inject(NetworkStatusService);
  private db: OfflineDb = inject(OFFLINE_DB, { optional: true }) ?? db;

  /** El código del rechazo definitivo cuando hay UNO: de él sale el título. Se lee al cambiar la cuenta. */
  readonly codigoDelUnico = signal<string | null>(null);
  private readonly leerCodigo = effect(() => {
    if (this.net.rechazadasDefinitivas() !== 1) { this.codigoDelUnico.set(null); return; }
    void this.offlineOrders.rechazadas()
      .then((l) => this.codigoDelUnico.set(l[0]?.evento.codigo ?? null))
      .catch(() => this.codigoDelUnico.set(null));
  });

  /** Los nombres del catálogo guardado; uno que ya no esté sale «Sin nombre» (neutro: «producto» es término del perfil). */
  private async nombresDe(claves: [string, string][]): Promise<Map<string, string>> {
    const productos = claves.length ? await this.db.catalogoProductos.bulkGet(claves).catch(() => []) : [];
    return new Map(productos.filter((p) => !!p).map((p) => [p!.idProduct, p!.nameProduct]));
  }
  private offlineOrders = inject(OfflineOrderService);
  private session = inject(SessionService);

  readonly reintentando = signal(false);

  /** M34 C1: la alerta del rango de numeración (la calcula el -mt una vez al día). Solo con el módulo de FE. */
  readonly alertaDeRango = signal<string | null>(null);
  private readonly features = inject(FeatureService);
  private readonly adquirentes = inject(AdquirenteService);
  private readonly leerRango = (() => {
    if (this.features.has('facturacion_electronica')) void this.adquirentes.alertaDeRango().then((a) => this.alertaDeRango.set(a));
  })();

  /** Producto b5af7f5: sin cifras, solo qué hacer. NO_ENCONTRADO aún no tiene texto: no se pinta. */
  readonly textoDelRango = computed<string | null>(() => ({
    VENCIDO: 'La numeración de facturas electrónicas venció. Avisa al administrador: mientras no se renueve, las facturas que se pidan no se pueden emitir.',
    AGOTADO: 'Se acabaron los números de factura electrónica. Avisa al administrador: mientras no se renueven, las facturas que se pidan no se pueden emitir.',
    POR_AGOTARSE: 'Quedan pocos números de factura electrónica. Avisa al administrador.',
    POR_VENCER: 'La numeración de facturas electrónicas vence pronto. Avisa al administrador.',
  } as Record<string, string>)[this.alertaDeRango() ?? ''] ?? null);

  /** M34 A7 (Producto c16f6a5): la factura se pide al subir la venta; no cita plazos de la DIAN. */
  readonly facturasAtrasadas = computed<string | null>(() => {
    const n = this.net.facturasAtrasadas();
    if (n <= 0) return null;
    return n === 1
      ? '1 venta con factura electrónica lleva más de 24 horas sin subirse. Su factura no se puede emitir hasta que se suba: conecta este equipo a internet y deja el POS abierto.'
      : `${n} ventas con factura electrónica llevan más de 24 horas sin subirse. Su factura no se puede emitir hasta que se suban: conecta este equipo a internet y deja el POS abierto.`;
  });

  /** Reintento manual de las órdenes rechazadas (el automático corre al reconectar). */
  async reintentar(): Promise<void> {
    if (this.reintentando()) return;
    this.reintentando.set(true);
    try {
      await this.offlineOrders.retryFailed();
    } finally {
      await this.net.refresh();
      this.reintentando.set(false);
    }
  }

  /**
   * F4.9b — «Ver venta»: qué se rechazó y por qué, con los datos para decidir. Qué
   * hacer con la mercancía ya entregada (cobrar de contado, anular, esperar) no lo
   * decide el POS todavía.
   */
  async verRechazadas(): Promise<void> {
    const lista = await this.offlineOrders.rechazadas();
    const esc = (t: string) => (t ?? '').replace(/[<>&]/g, '');
    const nombres = await this.nombresDe(lista.flatMap(({ venta }) => (venta?.items ?? []).map((i) => [venta!.tenantId, i.productId] as [string, string])));
    const filas = lista.map(({ evento, venta }) => {
      const pedido = venta ? lineasPedidas(venta.items ?? [], nombres).map((l) => `<br><span>${esc(l)}</span>`).join('') : '';
      const hora = venta ? new Date(venta.createdAt).toLocaleString('es-CO') : '';
      const total = venta ? pesos(venta.total) : '';
      const cliente = venta?.clienteDocumento ? ` · cliente ${esc(venta.clienteDocumento)}` : '';
      return `<li style="margin-bottom:.5rem"><strong>${esc(hora)} · ${esc(total)}${cliente}</strong><br>` +
        `<span style="color:#b91c1c">${esc(evento.lastError ?? 'Rechazada')}</span>${pedido}</li>`;
    }).join('');
    await Swal.fire({
      icon: 'warning',
      title: 'Ventas que el servidor rechazó',
      html: `<ul style="text-align:left;padding-left:1rem;font-size:.85rem">${filas || '<li>No hay ventas rechazadas.</li>'}</ul>` +
        `<p style="font-size:.8rem;color:#64748b">Siguen guardadas en este equipo. No se reintentan solas.</p>`,
      confirmButtonText: 'Entendido',
    });
  }

  readonly state = computed<{ kind: 'offline' | 'failed' | 'syncing' | 'otros'; text: string; definitivas: number; total: number } | null>(() => {
    const online = this.net.online();
    const pending = this.net.pending();
    const failed = this.net.failed();
    const deOtros = this.net.deOtrosNegocios();
    const deOtrosVendedores = this.net.deOtrosVendedores();
    const definitivas = this.net.rechazadasDefinitivas();

    if (!online) {
      return {
        definitivas: 0, total: 0,
        kind: 'offline',
        text:
          'Sin conexión. En espera de sincronización' +
          (pending ? ` (${pending} orden${pending === 1 ? '' : 'es'})` : ''),
      };
    }
    if (failed > 0) {
      // Mensaje accionable (N2/6.2): antes decía "revisa tu sesión" sin decir
      // qué revisar ni cómo, y no se iba solo. Ahora dice qué pasó, qué hacer, y
      // desaparece cuando la resincronización automática las drena.
      if (definitivas === failed) {
        // F4.9b: todas son rechazos que no se arreglan reintentando: se dice el motivo real.
        return {
          kind: 'failed', definitivas, total: failed,
          // El título del código de ESE rechazo (ECM, 24/09): antes era siempre el de insolvencia.
          text: definitivas === 1
            ? tituloDelRechazo(this.codigoDelUnico())
            : `${definitivas} ventas rechazadas por el servidor: revisa el motivo`,
        };
      }
      return {
        kind: 'failed', definitivas, total: failed,
        text:
          `${failed} orden${failed === 1 ? '' : 'es'} sin sincronizar. ` +
          'Si tu sesión venció, vuelve a iniciar sesión',
      };
    }
    if (pending > 0) {
      return { kind: 'syncing', definitivas: 0, total: 0, text: `Sincronizando ${pending} orden${pending === 1 ? '' : 'es'}…` };
    }
    if (deOtrosVendedores > 0) {
      // F2.3: con sesión de vendedor, las de otro vendedor esperan a su dueño.
      // No es un fallo ni se arregla reintentando.
      const una = deOtrosVendedores === 1;
      return {
        definitivas: 0, total: 0,
        kind: 'otros',
        text: `${deOtrosVendedores} venta${una ? '' : 's'} de otro vendedor en este equipo: ` +
          `se envía${una ? '' : 'n'} cuando ese vendedor inicie sesión`,
      };
    }
    if (deOtros > 0 && !this.session.isAuthenticated()) {
      // Sin sesión todo lo que espera cuenta como «de otro negocio», pero casi siempre es de la misma cajera: la que
      // cobró justo cuando venció su sesión (c8eeead). «De otra cuenta» era falso (texto de ECM, 19/09).
      const una = deOtros === 1;
      return {
        definitivas: 0, total: 0,
        kind: 'otros',
        text: una
          ? '1 venta sin enviar en este equipo. Se envía al iniciar sesión con la cuenta que la hizo.'
          : `${deOtros} ventas sin enviar en este equipo. Se envían al iniciar sesión con la cuenta que las hizo.`,
      };
    }
    if (deOtros > 0) {
      // F2.2: no se envían con esta sesión (irían al negocio equivocado), pero
      // tampoco se esconden: si nadie entra con esa cuenta, nunca llegan.
      return {
        definitivas: 0, total: 0,
        kind: 'otros',
        text:
          `${deOtros} venta${deOtros === 1 ? '' : 's'} de otra cuenta sin enviar en este equipo. ` +
          'Se envía' + (deOtros === 1 ? '' : 'n') + ' al entrar con esa cuenta',
      };
    }
    return null;
  });
}
